import { useState } from 'react';
import { comments as mockComments, type Comment } from '@/mocks/manga';

interface CommentsProps {
  mangaId: string;
}

export default function Comments({ mangaId }: CommentsProps) {
  const [list, setList] = useState<Comment[]>(mockComments.filter((c) => c.mangaId === mangaId));
  const [likedIds, setLikedIds] = useState<Set<string>>(new Set());
  const [content, setContent] = useState('');

  const toggleLike = (id: string) => {
    setLikedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const text = content.trim();
    if (!text) return;
    const newComment: Comment = {
      id: `local-${Date.now()}`,
      mangaId,
      user: '本站读者',
      content: text.slice(0, 200),
      time: '刚刚',
      likes: 0,
    };
    setList((prev) => [newComment, ...prev]);
    setContent('');
  };

  return (
    <section className="rounded-lg bg-background-50 p-4 md:p-6" aria-label="读者评论">
      <h2 className="font-heading text-lg text-foreground-950 md:text-xl">
        读者评论
        <span className="ml-2 text-sm text-foreground-500">{list.length} 条</span>
      </h2>

      <form onSubmit={submit} className="mt-4 flex flex-col gap-3 rounded-lg bg-background-100 p-4">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={200}
          rows={3}
          placeholder="说说你的看法吧～（200字以内）"
          aria-label="发表评论"
          className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
        />
        <div className="flex items-center justify-between gap-3">
          <span className="text-xs text-foreground-500">{content.length}/200</span>
          <button
            type="submit"
            className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            发表评论
          </button>
        </div>
      </form>

      <ul className="mt-5 space-y-4">
        {list.map((comment) => {
          const liked = likedIds.has(comment.id);
          return (
            <li key={comment.id} className="flex gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-base text-primary-700">
                {comment.user.slice(0, 1)}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-foreground-950">{comment.user}</span>
                  <span className="text-xs text-foreground-500">{comment.time}</span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-foreground-800">{comment.content}</p>
                <button
                  type="button"
                  onClick={() => toggleLike(comment.id)}
                  className={`mt-1.5 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs transition-colors ${
                    liked
                      ? 'bg-primary-100 text-primary-700'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} aria-hidden="true" />
                  赞 {comment.likes + (liked ? 1 : 0)}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}